import React, { useState, useEffect } from 'react';
import { MdCheck, MdClose, MdDownload } from 'react-icons/md';
import MainLayout from '../components/Layout/MainLayout';
import hrService from '../services/hrService';
import { toast } from 'react-toastify';

/**
 * ResignationApprovalPage Component
 * Admin page to manage and approve/reject resignation requests
 */
const ResignationApprovalPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('PENDING');
  const [approvalModal, setApprovalModal] = useState(false);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch resignation requests
  useEffect(() => {
    fetchResignationRequests();
  }, [page, selectedStatus]);

  const fetchResignationRequests = async () => {
    setLoading(true);
    try {
      const response = await hrService.getResignationRequests({
        page,
        limit: pageSize,
        status: selectedStatus
      });

      if (response.success || response.data) {
        setRequests(response.data?.requests || []);
        setTotalPages(response.data?.pagination?.pages || 1);
      } else {
        toast.error('Failed to fetch resignation requests');
      }
    } catch (error) {
      console.error('Error fetching resignation requests:', error);
      toast.error('Failed to fetch resignation requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setApprovalNotes('');
    setApprovalModal(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectionModal(true);
  };

  const submitApproval = async () => {
    if (!selectedRequest) return;

    setSubmitting(true);
    try {
      const response = await hrService.approveResignation(selectedRequest._id, {
        notes: approvalNotes.trim()
      });

      if (response.success || response.data) {
        toast.success('Resignation approved. Employee can now finalize their resignation.');
        setApprovalModal(false);
        fetchResignationRequests();
      } else {
        toast.error(response.error || 'Failed to approve resignation');
      }
    } catch (error) {
      console.error('Error approving resignation:', error);
      toast.error(error.response?.data?.error || 'Failed to approve resignation');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRejection = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setSubmitting(true);
    try {
      const response = await hrService.rejectResignation(selectedRequest._id, {
        rejectionReason: rejectionReason.trim()
      });

      if (response.success || response.data) {
        toast.success('Resignation rejected. Employee can resubmit if needed.');
        setRejectionModal(false);
        fetchResignationRequests();
      } else {
        toast.error(response.error || 'Failed to reject resignation');
      }
    } catch (error) {
      console.error('Error rejecting resignation:', error);
      toast.error(error.response?.data?.error || 'Failed to reject resignation');
    } finally {
      setSubmitting(false);
    }
  };

  const closeApprovalModal = () => {
    setApprovalModal(false);
    setSelectedRequest(null);
    setApprovalNotes('');
  };

  const closeRejectionModal = () => {
    setRejectionModal(false);
    setSelectedRequest(null);
    setRejectionReason('');
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'FINALIZED':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <MainLayout>
      <div className="p-6 bg-background min-h-screen">
        <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-charcoal mb-6">Resignation Requests</h1>

        {/* Status Filter */}
        <div className="mb-6 flex gap-3 flex-wrap">
          {['PENDING', 'APPROVED', 'REJECTED', 'FINALIZED'].map(status => (
            <button
              key={status}
              onClick={() => {
                setSelectedStatus(status);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
                selectedStatus === status
                  ? 'bg-primary text-white'
                  : 'bg-white text-charcoal border border-border hover:bg-gray-50'
              }`}
            >
              {status} ({requests.length})
            </button>
          ))}
        </div>

        {/* Requests Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-text-secondary">Loading resignation requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-border">
            <p className="text-text-secondary">No resignation requests found</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-charcoal">Employee</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-charcoal">Position</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-charcoal">Submitted</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-charcoal">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-charcoal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request._id} className="border-b border-border hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-charcoal">
                            {request.userId?.fullName || request.userId?.name || 'Unknown Employee'}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {request.userId?.employeeId || request.userId?.phoneNumber || 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-charcoal">{request.userId?.role || 'N/A'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-charcoal">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {request.resignationLetterUrl && (
                            <a
                              href={request.resignationLetterUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title="View resignation letter"
                            >
                              <MdDownload size={16} />
                            </a>
                          )}
                          {request.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => handleApproveClick(request)}
                                className="p-2 rounded-md bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                                title="Approve resignation"
                              >
                                <MdCheck size={16} />
                              </button>
                              <button
                                onClick={() => handleRejectClick(request)}
                                className="p-2 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                title="Reject resignation"
                              >
                                <MdClose size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-text-secondary">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-md bg-white border border-border text-charcoal disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-md bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Approval Modal */}
      {approvalModal && selectedRequest && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={closeApprovalModal}>
          <div className="bg-white rounded-xl p-6 max-w-[500px] w-[90%] shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-3 text-xl font-semibold text-green-600">Approve Resignation</h3>
            <p className="m-0 mb-4 text-text-secondary text-sm">
              Employee: <strong>{selectedRequest.userId?.fullName || selectedRequest.userId?.name || 'Unknown Employee'}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Reason for Resignation
              </label>
              <p className="text-sm text-charcoal bg-gray-50 p-3 rounded-md border border-border">
                {selectedRequest.resignationReason}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Approval Notes (Optional)
              </label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes for the employee..."
                className="w-full p-3 border border-border rounded-md resize-none h-20 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={submitting}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={closeApprovalModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={submitApproval}
                disabled={submitting}
              >
                {submitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModal && selectedRequest && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={closeRejectionModal}>
          <div className="bg-white rounded-xl p-6 max-w-[500px] w-[90%] shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-3 text-xl font-semibold text-red-600">Reject Resignation</h3>
            <p className="m-0 mb-4 text-text-secondary text-sm">
              Employee: <strong>{selectedRequest.userId?.fullName || selectedRequest.userId?.name || 'Unknown Employee'}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Reason for Resignation
              </label>
              <p className="text-sm text-charcoal bg-gray-50 p-3 rounded-md border border-border">
                {selectedRequest.resignationReason}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this resignation is being rejected..."
                className="w-full p-3 border border-border rounded-md resize-none h-20 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={submitting}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={closeRejectionModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={submitRejection}
                disabled={submitting || !rejectionReason.trim()}
              >
                {submitting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </MainLayout>
  );
};

export default ResignationApprovalPage;
